import React from 'react';
import { Link } from 'react-router-dom';
import type { PlatformUser, UserSortField, SortOrder } from '../types';
import {
  formatCredits,
  getPlanInfo,
  formatUserStatus,
  getUserDisplayName,
} from '../utils';
import { Avatar, Badge, Button, Checkbox, Icon } from '../../components';
import { IconName } from '@ui-library/Icon/Icon.type';

// ─────────────────────────────────────────────────────────────
// SortLabel
// A clickable column-header label with an asc/desc indicator. When
// `onSort` is not provided the header renders as plain static text,
// keeping UserListView backward-compatible for non-sortable usages.
// ─────────────────────────────────────────────────────────────

interface SortLabelProps {
  label: string;
  field: UserSortField;
  sortBy?: UserSortField;
  sortOrder?: SortOrder;
  onSort?: (field: UserSortField, order: SortOrder) => void;
}

const SortLabel: React.FC<SortLabelProps> = ({ label, field, sortBy, sortOrder, onSort }) => {
  const baseText = 'text-xs uppercase tracking-wider whitespace-nowrap';

  if (!onSort) {
    return <span className={`${baseText} font-medium text-text-tertiary`}>{label}</span>;
  }

  const active = sortBy === field;
  const ascending = sortOrder === 'asc';

  // Each caret is its own click target: clicking ▲ sorts ascending, clicking ▼ sorts
  // descending — the user picks the direction directly, not via a toggle. The caret
  // matching the active direction is full-dark (text-text-primary); the other — and
  // both carets on inactive columns — are faint. inheritColor makes each caret adopt
  // its button's text colour.
  const faint = 'text-text-quaternary opacity-40';

  // Clicking the label name is a convenience toggle: activate ascending, or flip if
  // this column is already the active sort.
  const labelOrder: SortOrder = active && ascending ? 'desc' : 'asc';

  return (
    <span className="inline-flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="very-small"
        onClick={() => onSort(field, labelOrder)}
        ariaLabel={`Sort by ${label}, ${active ? (ascending ? 'currently ascending' : 'currently descending') : 'not sorted'}`}
        title={active ? `Sorted ${ascending ? 'ascending' : 'descending'}` : `Sort by ${label}`}
        label={
          <span className={`${baseText} font-medium text-text-tertiary hover:text-text-secondary`}>
            {label}
          </span>
        }
      />
      <span className="inline-flex flex-col items-center leading-none" style={{ gap: '1px' }}>
        <Button
          type="button"
          variant="ghost"
          size="very-small"
          onClick={() => onSort(field, 'asc')}
          ariaLabel={`Sort ${label} ascending`}
          title={`Sort ${label} ascending`}
          className="!p-0"
          label={
            <span className={`flex ${active && ascending ? 'text-text-primary' : faint} hover:text-text-secondary`}>
              <Icon name={IconName.CaretUp} size="xxs" inheritColor />
            </span>
          }
        />
        <Button
          type="button"
          variant="ghost"
          size="very-small"
          onClick={() => onSort(field, 'desc')}
          ariaLabel={`Sort ${label} descending`}
          title={`Sort ${label} descending`}
          className="!p-0"
          label={
            <span className={`flex ${active && !ascending ? 'text-text-primary' : faint} hover:text-text-secondary`}>
              <Icon name={IconName.CaretDown} size="xxs" inheritColor />
            </span>
          }
        />
      </span>
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// UserRowSkeleton
// Mirrors ProjectCardSkeleton: staggered animate-fade-in-up with
// skeleton-shimmer blocks matching the real row's layout.
// ─────────────────────────────────────────────────────────────

const UserRowSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <tr
    className="opacity-0"
    style={{ animation: `fadeIn 0.3s ease-out ${index * 0.08}s forwards` }}
  >
    {/* Checkbox */}
    <td className="px-bg-v2-md py-bg-v2-xl text-center">
      <div className="w-4 h-4 skeleton-shimmer rounded mx-auto" />
    </td>

    {/* User (avatar + name + email) */}
    <td className="px-bg-v2-xl py-bg-v2-xl overflow-hidden">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full skeleton-shimmer flex-shrink-0" />
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <div className="h-4 skeleton-shimmer rounded w-36" />
          <div className="h-3 skeleton-shimmer rounded w-48" />
        </div>
      </div>
    </td>

    {/* Status */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="h-5 skeleton-shimmer rounded-full w-16" />
    </td>

    {/* Plan */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="h-5 skeleton-shimmer rounded-full w-20" />
    </td>

    {/* Balance */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="h-4 skeleton-shimmer rounded w-16" />
    </td>

    {/* Lifetime usage */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="h-4 skeleton-shimmer rounded w-16" />
    </td>

    {/* Created */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="h-4 skeleton-shimmer rounded w-24" />
    </td>

    {/* Actions */}
    <td className="px-bg-v2-xl py-bg-v2-xl">
      <div className="flex items-center justify-end gap-2">
        <div className="h-4 skeleton-shimmer rounded w-20" />
        <div className="h-4 skeleton-shimmer rounded w-20" />
        <div className="h-4 skeleton-shimmer rounded w-24" />
      </div>
    </td>
  </tr>
);

// ─────────────────────────────────────────────────────────────
// UserRow
// A single data row — extracted so UserListView stays readable.
// ─────────────────────────────────────────────────────────────

interface UserRowProps {
  user: PlatformUser;
  index: number;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onQuickAction: (user: PlatformUser, action: 'addCredits' | 'changePlan') => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, index, isSelected, onSelect, onQuickAction }) => {
  const userId = user.id || user.email;
  const status = formatUserStatus(user);
  const plan = getPlanInfo(user.billing.plan_type);

  const createdDate = user.createdTimestamp
    ? (() => {
        const d = new Date(user.createdTimestamp);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}/${dd}/${d.getFullYear()}`;
      })()
    : 'N/A';

  return (
    <tr
      key={userId}
      className="hover:bg-bg-secondary opacity-0"
      style={{ animation: `fadeIn 0.35s ease-out ${index * 0.05}s forwards` }}
    >
      <td className="px-bg-v2-md py-bg-v2-xl text-center">
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(userId, e.target.checked)}
          size="sm"
        />
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl overflow-hidden">
        <div className="flex items-center min-w-0">
          <Avatar user={user} size="lg" />
          <div className="ml-bg-v2-lg min-w-0 overflow-hidden">
            <div className="flex items-center gap-bg-v2-sm min-w-0">
              <div
                className="text-sm font-medium text-text-primary truncate"
                title={getUserDisplayName(user)}
              >
                {getUserDisplayName(user)}
              </div>
              {user.roles?.includes('guest') && <Badge tone="neutral">Guest</Badge>}
            </div>
            <div className="text-sm text-text-tertiary truncate" title={user.email}>
              {user.email}
            </div>
          </div>
        </div>
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl">
        <span className={`inline-flex px-bg-v2-md py-bg-v2-xs text-xs font-semibold rounded-bg-v2-full whitespace-nowrap ${status.color}`}>
          {status.text}
        </span>
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl">
        <span className={`inline-flex px-bg-v2-md py-bg-v2-xs text-xs font-semibold rounded-bg-v2-full whitespace-nowrap ${plan.color}`}>
          {plan.label}
        </span>
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl text-sm text-text-primary whitespace-nowrap">
        {formatCredits(user.billing.total_credits)}
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl text-sm text-text-primary whitespace-nowrap">
        {formatCredits(user.billing.total_lifetime_usage)}
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl text-sm text-text-tertiary whitespace-nowrap">
        {createdDate}
      </td>

      <td className="px-bg-v2-xl py-bg-v2-xl text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-bg-v2-md">
          <Link
            to={`/admin/users/${encodeURIComponent(userId)}`}
            className="text-sm text-text-brand-primary hover:text-text-brand-secondary font-medium"
          >
            View Details
          </Link>
          <span className="text-text-disabled">|</span>
          <button
            onClick={() => onQuickAction(user, 'addCredits')}
            className="text-sm text-text-success-primary hover:text-fg-success-primary font-medium"
          >
            Add Credits
          </button>
          <span className="text-text-disabled">|</span>
          <button
            onClick={() => onQuickAction(user, 'changePlan')}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Change Plan
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────────
// UserListView — the main export
//
// Props:
//   users            – filtered list to display
//   loading          – shows skeleton rows instead of real rows
//   error            – (unused here; parent renders error banner)
//   selectedUsers    – Set of selected user ids
//   onSelectUser     – toggle a single row checkbox
//   onSelectAll      – toggle the header "select all" checkbox
//   onQuickAction    – bubble up "Add Credits" / "Change Plan"
//   hasActiveFilters – shows "Clear filters" link in empty state
//   onClearFilters   – callback for the clear-filters button
//   filterKey        – changing this value re-mounts tbody, re-triggering
//                      stagger animations (pass `${search}-${enabled}-${plan}`)
//   skeletonCount    – number of skeleton rows to show (default 8)
// ─────────────────────────────────────────────────────────────

export interface UserListViewProps {
  users: PlatformUser[];
  loading: boolean;
  selectedUsers: Set<string>;
  onSelectUser: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onQuickAction: (user: PlatformUser, action: 'addCredits' | 'changePlan') => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  filterKey: string;
  skeletonCount?: number;
  /** Override the outer wrapper's className. Defaults to the standard card style. */
  wrapperClassName?: string;
  /** Active sort column, or undefined for the default (unsorted) order. */
  sortBy?: UserSortField;
  /** Active sort direction. */
  sortOrder?: SortOrder;
  /** Called when a sortable header/caret is clicked, with the chosen direction. Omit to render static headers. */
  onSort?: (field: UserSortField, order: SortOrder) => void;
}

export const UserListView: React.FC<UserListViewProps> = ({
  users,
  loading,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onQuickAction,
  hasActiveFilters,
  onClearFilters,
  filterKey,
  skeletonCount = 8,
  wrapperClassName = 'bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm overflow-hidden',
  sortBy,
  sortOrder,
  onSort,
}) => {
  const allSelected = selectedUsers.size === users.length && users.length > 0;
  const sortProps = { sortBy, sortOrder, onSort };

  return (
    <div className={wrapperClassName}>
      <div className="w-full overflow-x-auto">
        <table className="w-full divide-y divide-border-secondary table-fixed" style={{ minWidth: '990px' }}>
          <colgroup>
            <col className="w-10" />
            <col />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            {/* Balance — a touch wider so the bold+chevron active label doesn't crowd the next column. */}
            <col className="w-[110px]" />
            {/* Life Time Usage — wider so the header stays on one line (see SortLabel whitespace-nowrap). */}
            <col className="w-[140px]" />
            <col className="w-[100px]" />
            <col className="w-[220px]" />
          </colgroup>

          <thead className="bg-bg-secondary">
            <tr>
              <th className="px-bg-v2-md py-bg-v2-lg text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  size="sm"
                />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                {/* Name + email share this cell (name over email); only Name is sortable. */}
                <SortLabel label="Name" field="name" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                <SortLabel label="Status" field="status" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                <SortLabel label="Plan" field="plan" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                <SortLabel label="Balance" field="total_credits" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                <SortLabel label="Life Time Usage" field="lifetime_usage" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left">
                <SortLabel label="Created" field="created_date" {...sortProps} />
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          {/* key on tbody causes React to fully remount it on filter changes,
              restarting all stagger animations from index 0. */}
          {loading ? (
            <tbody key="skeleton" className="bg-bg-primary divide-y divide-border-secondary">
              {[...Array(skeletonCount)].map((_, i) => (
                <UserRowSkeleton key={i} index={i} />
              ))}
            </tbody>
          ) : (
            <tbody key={filterKey} className="bg-bg-primary divide-y divide-border-secondary">
              {users.map((user, index) => (
                <UserRow
                  key={user.id || user.email}
                  user={user}
                  index={index}
                  isSelected={selectedUsers.has(user.id || user.email)}
                  onSelect={onSelectUser}
                  onQuickAction={onQuickAction}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Empty state — only shown when not loading and list is empty */}
      {!loading && users.length === 0 && (
        <div className="text-center py-bg-v2-6xl">
          <p className="text-text-tertiary">No users found matching your criteria</p>
          {hasActiveFilters && (
            <Button
              label="Clear filters"
              variant="link"
              size="small"
              onClick={onClearFilters}
              className="mt-bg-v2-md"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UserListView;
